import { DADOS_URL, FIREBASE_BASE_URL } from "../ducks/consts";
import timeoutError from "./timeoutError";
import { notifyErrorTracker } from "../services/error-tracker";

// Lê do Realtime Database direto (`firebaseio.com`) e, se ele não responde, da
// rota /_dados do próprio app (function `dados`), que lê os mesmos dados do
// lado do servidor. Filtro de roteador, DNS ou provedor às vezes bloqueia o
// firebaseio.com: com o fallback só no timeout, cada leitura esperava 5s, e uma
// recusa imediata (DNS bloqueado) virava tela de erro.

// Sem os cabeçalhos do Firebase neste tempo, a rota própria entra em paralelo e
// vale a que responder primeiro. Conta os cabeçalhos, e não o corpo: cardápio
// grande em 3G lento demora no download, e aí o Firebase está acessível.
const HEDGE_MS = 2500;
const TIMEOUT_MS = 15000;

// Firebase inacessível nesta página: as próximas leituras vão direto para a
// rota própria, e só a primeira paga a espera.
let firebaseInacessivel = false;

// async: o erro de HTTP sai como promessa rejeitada, e não como throw dentro
// do `then` de quem chama (que escaparia do tratamento)
async function lerCorpo(response) {
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} em ${response.url}`);
  }
  return timeoutError(TIMEOUT_MS, response.json());
}

// O CDN guarda a rota própria por 10s. A `versao` (o publicado/time) entra
// na URL quando a leitura é por causa de uma republicação: a chave de cache
// muda, e a loja não recebe o cardápio de antes de publicar. A function ignora
// a query.
function lerRotaPropria(path, versao) {
  const query = versao ? `?v=${encodeURIComponent(versao)}` : "";
  return timeoutError(TIMEOUT_MS, fetch(`${DADOS_URL}${path}${query}`)).then(
    lerCorpo,
  );
}

// Uma vez por página: o IP do evento no Bugsnag mostra o provedor da rede que
// bloqueia.
function marcarInacessivel(tipo, path) {
  if (firebaseInacessivel) return;
  firebaseInacessivel = true;
  notifyErrorTracker(new Error("Firebase inacessível, usando /_dados"), {
    severity: "info",
    metadata: { firebase: { tipo, path } },
  });
}

/**
 * @param {string} path caminho sob /menus
 * @param {{ versao?: number }} [options] `versao`: o publicado/time que motivou
 *   a leitura, para não pegar o cache do CDN de antes da republicação.
 */
export default function getFirebaseData(path, { versao } = {}) {
  if (firebaseInacessivel) {
    return lerRotaPropria(path, versao);
  }

  // Sem Promise.any: o WebView antigo do totem e do POS não tem.
  return new Promise((resolve, reject) => {
    let terminou = false;
    let firebaseRespondeu = false;
    let firebaseFalhou = false;
    let reserva = null;
    let reservaFalhou = false;
    let erroReserva = null;
    let semCabecalhos = false;

    const concluir = (dados) => {
      if (terminou) return;
      terminou = true;
      resolve(dados);
    };

    const usarReserva = () => {
      if (reserva) return;
      reserva = lerRotaPropria(path, versao).then(
        (dados) => {
          // ganhou do Firebase que nem mandou os cabeçalhos: rede bloqueando
          if (semCabecalhos && !firebaseRespondeu && !terminou) {
            marcarInacessivel("sem resposta", path);
          }
          concluir(dados);
        },
        (erro) => {
          reservaFalhou = true;
          erroReserva = erro;
          if (firebaseFalhou && !terminou) {
            terminou = true;
            reject(erro);
          }
        },
      );
    };

    const falhaFirebase = () => {
      firebaseFalhou = true;
      if (reservaFalhou && !terminou) {
        terminou = true;
        reject(erroReserva);
        return;
      }
      usarReserva();
    };

    const hedge = setTimeout(() => {
      semCabecalhos = true;
      usarReserva();
    }, HEDGE_MS);

    timeoutError(TIMEOUT_MS, fetch(`${FIREBASE_BASE_URL}${path}.json`)).then(
      (response) => {
        clearTimeout(hedge);
        firebaseRespondeu = true;
        // HTTP de erro ou corpo que não chega: o Firebase está acessível, só
        // esta leitura falhou. Não marca a página.
        return lerCorpo(response).then(concluir, falhaFirebase);
      },
      () => {
        clearTimeout(hedge);
        // erro de rede (DNS, conexão recusada) ou nem os cabeçalhos em 15s
        marcarInacessivel("rede", path);
        falhaFirebase();
      },
    );
  });
}
